import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * Shows the per-candle price for the quantity in the product page's quantity stepper, plus a
 * static reference table of all four rates. Display only: the real price is always set by
 * Shopify's automatic discounts at cart/checkout (data-tiers, "minQuantity:percent,...", must
 * match them — see the Liquid comment in nap-quantity-pricing.liquid).
 */
class NapQuantityPricing extends HTMLElement {
  connectedCallback() {
    this.unitPrice = Number(this.dataset.unitPrice); // minor units
    this.tiers = (this.dataset.tiers || '')
      .split(',')
      .map((pair) => pair.split(':').map(Number))
      .filter(([min, pct]) => min > 0 && pct > 0)
      .sort((a, b) => a[0] - b[0]);

    this.format = (/** @type {number} */ cents) =>
      formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? '');

    // Static reference rows: same tiers, computed once.
    const priceAt = (/** @type {number} */ qty) => {
      let percent = 0;
      for (const [min, pct] of this.tiers) if (qty >= min) percent = pct;
      return Math.round((this.unitPrice * (100 - percent)) / 100);
    };
    for (const n of [2, 3, 4]) {
      const cell = this.querySelector(`[ref="row${n}"]`);
      if (cell) cell.textContent = `${this.format(priceAt(n))} EACH`;
    }

    this.update = () => requestAnimationFrame(() => this.render());
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.update);
    document.addEventListener('input', this.update);
    document.addEventListener('change', this.update);
    document.addEventListener('click', this.update);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.update);
    document.removeEventListener('input', this.update);
    document.removeEventListener('change', this.update);
    document.removeEventListener('click', this.update);
  }

  get quantity() {
    const input =
      this.closest('product-form, form, section')?.querySelector('input[name="quantity"]') ??
      document.querySelector('.product-form input[name="quantity"], input[name="quantity"]');
    const value = parseInt(input?.value ?? '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  render() {
    const qty = this.quantity;
    let percent = 0;
    for (const [min, pct] of this.tiers) if (qty >= min) percent = pct;
    const unit = Math.round((this.unitPrice * (100 - percent)) / 100);

    const live = this.querySelector('[ref="unit"]');
    if (live) live.textContent = this.format(unit);

    // Bold whichever reference row matches the current quantity (tier 4 covers 4+).
    const activeTier = qty >= 4 ? 4 : qty;
    this.querySelectorAll('[ref="table"] .nap-quantity-pricing__row').forEach((row) => {
      row.classList.toggle('is-active', Number(row.getAttribute('data-tier')) === activeTier);
    });
  }
}

if (!customElements.get('nap-quantity-pricing')) customElements.define('nap-quantity-pricing', NapQuantityPricing);
